import { cn } from 'helpers/classnames';
import { COMPLETED, CS_CANCELLED, CUSTOMER_ACTION_REQUIRED, DELIVERED, PROCESSING, RETURNED, RETURNING, SUBMITTED } from 'constants/orderStatuses';
import {
  CANCEL_STATUSES,
  DELIVERED_STATUSES,
  getCurrentShipment,
  getCurrentShipmentMessage,
  getCurrentShipmentNumber,
  getLegacyStatusFromCompositeStatus,
  getTotalShipmentsNumber,
  RETURN_STATUSES,
  SHIPPED_STATUSES
} from 'helpers/MyAccountUtils';
import { convertExpirationTimeToString } from 'helpers/CheckoutUtils';
import ShippingStatus from 'components/account/ShippingStatus';
import { groupBy, toArray } from 'helpers/lodashReplacement';
import { evTrackAccountClick, evTrackOrderHistorySearch } from 'events/account';
import { evReturnInitiateClick } from 'events/returns';
import { myAccountLinkMap } from 'constants/amethystEnums';
import { track } from 'apis/amethyst';
import type { Order } from 'types/mafia';
import deliveryTruckBlueIcon from 'images/delivery_truck_blue.svg';
import deliveryTruckRedIcon from 'images/zappos/delivery_truck_red.svg';
import checkGreenIcon from 'images/check-green.svg';
import returnIcon from 'images/return.svg';
import returnRightIcon from 'images/return-right-arrow.svg';
import circleX from 'images/circle-x.svg';
import returnIconOrange from 'images/return-orange.svg';
import { DELAYED, IN_TRANSIT } from 'constants/shippingStatuses';

// eslint-disable-next-line css-modules/no-unused-class
import css from 'styles/components/account/orderTable.scss';

export function makeFormErrorMessage(
  errorDetails: { extraInformation: { fieldName: string; id: string; expirationTime: number }[] },
  { errorMessage = "We're very sorry, there was an issue processing your request.", displayErrorPretext = true } = {}
) {
  let additionalInfo: (JSX.Element | undefined)[] = [];
  let errMsg = errorMessage;
  // In the case there is a invalid field the error catcher places the invalid fields in the extraInformation object
  if (errorDetails?.extraInformation?.length) {
    const { extraInformation } = errorDetails;
    const { expirationTime } = extraInformation.find(({ expirationTime }) => expirationTime) || {};
    errMsg = "We're very sorry, please fix the following issues:";
    additionalInfo = extraInformation.map(info => {
      const { fieldName, id } = info;
      switch (fieldName) {
        case 'fullName':
        case 'name.fullName':
          return <li key="name">Name</li>;
        case 'addCreditCardNumber':
          switch (id) {
            case 'credit.card.verification.declined':
              if (expirationTime) {
                const hoursAndMinutes = convertExpirationTimeToString(expirationTime);
                return (
                  <li key="cardNumber">
                    Credit card verification declined. You won’t be able to add a card for the next {hoursAndMinutes} due to security reasons.
                  </li>
                );
              }

              return <li key="cardNumber">Credit card verification declined</li>;
            default:
              return <li key="cardNumber">Card number</li>;
          }
        case 'expirationMonth':
          return <li key="exprMonth">Expiration month</li>;
        case 'expirationDate':
          return <li key="exprDate">Expiration date</li>;
        case 'mailingAddress.addressLine1':
          return <li key="addrLine1">Address Line 1</li>;
        case 'mailingAddress.addressLine2':
          return <li key="addrLine2">Address Line 2</li>;
        case 'mailingAddress.city':
          return <li key="city">City</li>;
        case 'mailingAddress.stateOrRegion':
          return <li key="state">State/Region</li>;
        case 'mailingAddress.postalCode':
          return <li key="zipcode">Zip/Postal Code</li>;
        case 'phone.voice.primary.number':
          return <li key="phoneNumber">Phone Number</li>;
        default:
          return;
      }
    });
  }
  return (
    <>
      {displayErrorPretext && (
        <p>
          <strong>Oh No!</strong> {errMsg}
        </p>
      )}
      <ul>{additionalInfo}</ul>
    </>
  );
}

// returns a list of shipping statuses from an order
export const makeShippingStatusList = (
  order: Order,
  statusClassName: string,
  options: {
    filterByInTransit: boolean;
    filterCancelledStatuses: boolean;
    hideTrackingProgress: boolean;
    setToolTipVisibilityforMobile: boolean;
    onShipmentClick: <T>(...args: T[]) => T;
  }
) => {
  const {
    filterByInTransit = false,
    filterCancelledStatuses = false,
    hideTrackingProgress = false,
    setToolTipVisibilityforMobile,
    onShipmentClick = (f: any) => f
  } = options || {};

  const { lineItems, orderId, shipments } = order;
  const itemsByShipmentId = toArray(groupBy(lineItems, lineItem => lineItem.shipment?.id || 'other'));

  return itemsByShipmentId.map(lineItems => {
    const { id: shipmentId } = lineItems[0].shipment;
    const { compositeStatus, trackingAvailable } = getCurrentShipment(shipmentId, shipments);
    const currentShipmentNumber = getCurrentShipmentNumber(shipmentId, shipments);
    const shipmentMessage = getCurrentShipmentMessage(shipmentId, shipments);
    const status = getLegacyStatusFromCompositeStatus(compositeStatus);
    const totalShipments = getTotalShipmentsNumber(shipments);

    if ((filterCancelledStatuses && compositeStatus === CS_CANCELLED) || (filterByInTransit && !SHIPPED_STATUSES.includes(status))) {
      // if were filtering by in transit, and if the status is not part of the "In Transit" statuses, render nothing.
      return null;
    }

    return (
      <ShippingStatus
        planName={undefined}
        key={`${shipmentId}-${currentShipmentNumber}`}
        status={status}
        className={statusClassName}
        hideTrackingProgress={hideTrackingProgress}
        orderId={orderId}
        onShipmentClick={onShipmentClick.bind(this, order, shipmentId, compositeStatus)}
        shipmentId={shipmentId}
        totalShipments={totalShipments}
        currentShipmentNumber={currentShipmentNumber}
        trackingAvailable={trackingAvailable}
        setToolTipVisibilityforMobile={setToolTipVisibilityforMobile}
        shipmentMessage={shipmentMessage}
      />
    );
  });
};

export function getItemStatusClassName(status: string, isWFMPackagelessReturn = false) {
  // TODO: should remove this check for isWFMPackagelessReturn when backend is ready to handle WFM return status tracking. https://sim.amazon.com/issues/P76166925
  if (isWFMPackagelessReturn && status === CUSTOMER_ACTION_REQUIRED) {
    return cn(css.statusLabel, css.returned, 'zappos:body-m');
  }

  // Submitted and processing
  const zapposOverrides = 'zappos:bg-gray-100 zappos:text-gray-900';

  return cn(css.statusLabel, 'zappos:body-m', {
    [`${css.submitted} ${zapposOverrides}`]: status === SUBMITTED || status === COMPLETED,
    [`${css.processing} ${zapposOverrides}`]: status === PROCESSING,
    [css.shipped]: SHIPPED_STATUSES.includes(status) || DELIVERED_STATUSES.includes(status),
    [css.returned]: RETURN_STATUSES.includes(status),
    [css.cancelled]: CANCEL_STATUSES.includes(status)
  });
}

// click event handler used to attach to the window to capture generic click events and search events on /account and /orders
export const amethystWindowClickEventHandler = (
  e: {
    target: {
      dataset?: {
        amethystAccountLink?: string;
        amethystReturnBehaviorSurvey?: string;
      };
      parentNode: {
        dataset?: {
          amethystAccountLink?: string;
          amethystReturnBehaviorSurvey?: string;
        };
      };
    };
  },
  sourcePage: string
) => {
  const amethystAccountLink = e.target.dataset?.amethystAccountLink || e.target.parentNode.dataset?.amethystAccountLink;
  const amethystReturnBehaviorSurvey = e.target.dataset?.amethystReturnBehaviorSurvey || e.target.parentNode.dataset?.amethystReturnBehaviorSurvey;

  if (amethystAccountLink) {
    const linkType = parseInt(amethystAccountLink);

    if (linkType === myAccountLinkMap.SEARCH_ORDERS_CLICK) {
      // this also gets the term in addition of tracking clicks
      const orderSearchTerm = (document.getElementById('searchOrderHistory') as HTMLInputElement)?.value;
      track(() => [evTrackOrderHistorySearch, { keyword: orderSearchTerm, sourcePage }]);
    } else {
      track(() => [evTrackAccountClick, { clickType: linkType, sourcePage }]);
    }
  }

  if (amethystReturnBehaviorSurvey) {
    track(() => [evReturnInitiateClick, { returnInitiateSource: amethystReturnBehaviorSurvey }]);
  }
};

export function getItemShippingInfoClassName(status: string, itemState: string) {
  return cn(
    css.shippingInfoStatusLabel,
    {
      [css.viewReturnQrCodeLink]: status.toLowerCase() === DELIVERED && itemState === RETURNING
    },
    'zappos:body-m'
  );
}

export function getItemStatusIcon(status: string, itemState: string, resolutionOffering?: string, returnTrackingStatus?: string, message?: string) {
  if (
    SHIPPED_STATUSES.includes(status) &&
    itemState !== RETURNING &&
    resolutionOffering !== 'ReturnlessRefund' &&
    returnTrackingStatus !== 'REFUNDED'
  ) {
    return (
      <img
        src={message?.includes('delayed') ? deliveryTruckRedIcon : deliveryTruckBlueIcon}
        className={css.shippingInfoIcon}
        alt="Delivery truck"
        aria-hidden="true"
      />
    );
  } else if (RETURN_STATUSES.includes(status)) {
    return <img src={returnIcon} className={css.shippingInfoIcon} alt="Return" aria-hidden="true" />;
  } else if (CANCEL_STATUSES.includes(status)) {
    return <img src={circleX} className={css.shippingInfoIcon} alt="CircleX" aria-hidden="true" />;
  } else if (
    status.toLowerCase() === SUBMITTED ||
    status.toLowerCase() === COMPLETED ||
    DELIVERED_STATUSES.includes(status) ||
    (status === IN_TRANSIT && itemState === RETURNING)
  ) {
    switch (itemState) {
      case RETURNED:
        return <img src={returnIcon} className={css.shippingInfoIcon} alt="Return" aria-hidden="true" />;
      case RETURNING:
        return <img src={returnIconOrange} className={css.shippingInfoIcon} alt="Return" aria-hidden="true" />;
      case SUBMITTED:
        return <img src={returnRightIcon} className={css.shippingInfoIcon} alt="Return" aria-hidden="true" />;
      default:
        return <img src={checkGreenIcon} className={css.shippingInfoIcon} alt="Checkmark" aria-hidden="true" />;
    }
  } else if (status.toLowerCase() === PROCESSING) {
    return <img src={returnRightIcon} className={css.shippingInfoIcon} alt="Delivery truck" aria-hidden="true" />;
  } else if (resolutionOffering === 'ReturnlessRefund' && returnTrackingStatus === 'REFUNDED') {
    return <img src={returnIcon} className={css.shippingInfoIcon} alt="Return" aria-hidden="true" />;
  } else if (status === DELAYED || message?.includes('delayed')) {
    return itemState === RETURNING ? (
      <img src={returnIconOrange} className={css.shippingInfoIcon} alt="Return" aria-hidden="true" />
    ) : (
      <img src={deliveryTruckRedIcon} className={css.shippingInfoIcon} alt="Delivery truck" aria-hidden="true" />
    );
  }
  return '';
}
